/** Copyright (c) 2020 T.Rowe Price. All rights reserved. */

import './ExternalInvestmentConsentModal.scss';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Store from 'state/Store';

import {
  EXTERNAL_INVESTMENT_CONSENT_TYPES,
  isPriorConsentedClient,
  isProspectiveClient
} from 'state/external/ExternalInvestmentConsentTypes';
import {
  getDelayedLinkAccount,
  getExternalInvestmentConsent,
  getExternalInvestmentConsentStatus,
  getShowConsentModal
} from 'state/external/ExternalInvestmentConsentSelectors';
import {
  getExternalInvestmentStatus,
  updateExternalInvestmentConsent
} from 'services/Rest';
import PageLoadingSpinner from 'components/layouts/Navigation/PageLoadingSpinner';
import BaseModal from 'components/shared/Modal/BaseModal';
import { showConsentModal } from 'state/external/ExternalInvestmentConsentActions';
import { ConsentModalContent } from 'components/shared/Modal/ConsentModalContent';
import { getCustomer } from 'state/customer/CustomerSelectors';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { isError, isRequested, isReceived } from 'support/RequestSupport';
import { getCustomerWebView } from 'state/customer/CustomerSelectors';
import { getAllAccounts } from 'state/account/AccountSelectors';
import Content from 'services/Content';
import If from 'components/shared/If/If';
import { CONTENT_TYPES, getContentFromCMS } from 'services/ContentManagement/CMSUtils';
import { AEM } from 'services/Externals';

const ErrorMessage = () => {
  return (
    <div>
      <h2>Error Saving Changes</h2>
      <div>
        There was an error updating your settings. Upon your next login, you
        will be prompted again with the same options. We apologize for the
        inconvenience.
      </div>
    </div>
  );
};

const ExternalInvestmentConsentModal = ({
                                          accounts,
                                          consent,
                                          consentStatus,
                                          customer,
                                          delayLinkAccount,
                                          hideAnchor,
                                          title,
                                          containerClassName,
                                          showModal,
                                          requestStatus
                                        }) => {

  const [updateError, setUpdateError] = useState(false);

  const [prospectTerms, setProspectTerms] = useState('');


  const reset = () => {
    setUpdateError(false);
    Store.dispatch({
      type: 'CLEAR_REQUEST_STATUS',
      payload: {
        requestStatus: {
          setExternalInvestmentConsent: null
        }
      }
    });
  };

  const closeModal = () => {
    reset();
    Store.dispatch(showConsentModal(false));
  };

  useEffect(() => {
    getContentFromCMS(
      AEM.FRAGMENTS.yodleeConsentDisclosure,
      'description',
      CONTENT_TYPES.HTML,
      (root) => {
        return root['html'];
      }
    ).then((response) => {
      if(!response || response === null) {
        setProspectTerms(Content.yodleeServiceTermsProspect);
      } else {
        setProspectTerms(response);
      }
    }).catch(() => {
      setProspectTerms(Content.yodleeServiceTermsProspect);
    })
    if(prospectTerms === '') {
      setProspectTerms(Content.yodleeServiceTermsProspect);
    }
  }, []);

  useEffect(() => {
    if (requestStatus.customer === 'RECEIVED') {
      const { isRetirementAdvisoryServiceClient: isRas } = customer;
      getExternalInvestmentStatus(isRas);
    }
  }, [requestStatus.customer, customer]);

  if (consent || !showModal) {
    return null;
  }


  return (
    <BaseModal
      close={closeModal}
      closeTitle={isPriorConsentedClient(consentStatus) ? '' : 'Close'}
      containerClassName={containerClassName}
      hideAnchor={hideAnchor}
      hideClose={isPriorConsentedClient(consentStatus)}
      title={title}
      renderTitleContent={title}
      label="consent-overlay-modal"
      disableClickOff={isPriorConsentedClient(consentStatus)}
      isOpen={showModal}>
      <div>
        <If
          test={
            isRequested('setExternalInvestmentConsent') &&
            (!isError('setExternalInvestmentConsent') ||
              !isReceived('setExternalInvestmentConsent'))
          }>
          <PageLoadingSpinner name={'Consent Update'} />
        </If>
        {/* FIRST TIME CONSENT - PROSPECT */}
        <If
          test={
            (isProspectiveClient(consentStatus) ||
              (consentStatus === EXTERNAL_INVESTMENT_CONSENT_TYPES.NO_ACTION &&
                !consent)) || (!isProspectiveClient(consentStatus) &&
              isPriorConsentedClient(consentStatus))
          }>
          <ConsentModalContent
            content={prospectTerms}
            hasError={updateError || isError('setExternalInvestmentConsent')}
            errorMessage={<ErrorMessage />}
            title="Important Information"
            handleErrorClose={closeModal}
            handleNegative={closeModal}
            handlePositive={() => {
              updateExternalInvestmentConsent(true, accounts, consentStatus)
                .then(() => {
                  // if you need to do something on consent success
                  if (!updateError && delayLinkAccount) {
                    closeModal();
                  }
                })
                .catch(() => {
                  setUpdateError(true);
                });
            }}
            negativeLabel="Cancel"
            positiveLabel="I Agree"
          />
        </If>
      </div>
    </BaseModal>
  );
};

ExternalInvestmentConsentModal.defaultProps = {
  containerClassName: 'external-investment-consent-modal-container'
};

const mapStateToProps = (state) => ({
  accounts: getAllAccounts(state),
  consentStatus: getExternalInvestmentConsentStatus(state),
  consent: getExternalInvestmentConsent(state),
  delayLinkAccount: getDelayedLinkAccount(state),
  isCustomerWebView: getCustomerWebView(state),
  requestStatus: getRequestStatus(state),
  showModal: getShowConsentModal(state),
  customer: getCustomer(state)
});

export default connect(mapStateToProps)(ExternalInvestmentConsentModal);
